import { Observable, of } from 'rxjs';
import { switchMap, map, publishReplay, refCount, first } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { Params } from '@angular/router';

import { ILogServerData, Log } from '../log';
import { LogSuggestion } from '@misc/log-suggestion';
import { ILog } from '../log-view-model';

import { IMoment, moment } from '@lib/moment';

import { Session } from '@services/session';
import { ServerApi } from '@services/server-api';
import { DriversSearch } from '@services/drivers-search';
import { VehiclesSearch } from '@services/vehicles-search';
import { AssetsSearch } from '@services/assets-search.service';
import { ComplianceFeatureFlagService } from '@services/feature-flags/compliance';

import { objectToMap, extractResponseBody, fullName } from '@lib/utils';

import { IServerNavigation } from '@misc/detail-view-navigation/server-navigation.interface';
import { DpeDismissalReasonType } from '@misc/event-dismissal-modal/event-dismissal-modal.consts';

export interface ILogDetailData {
  log: Log;
  logWithSuggestion: Log;
  stagedLog: Log;
  logSuggestion: LogSuggestion;
  navigation: IServerNavigation;
}

export interface IServerLogDetail extends ILogServerData {
  _navigation: IServerNavigation;
}

export interface IIgnoreDriveTimePayload {
  offline_id: string;
  ignore_drive_time_duty_type: string;
  ignore: boolean;
}

export interface ILogCoachingServerData {
  id: number;
  coaching_status: string;
  note: ILogServerNote;
}

export interface ILogServerNote {
  id: number;
  body: string;
  created_at: string;
  updated_at: string;
  is_visible_to_driver: boolean;
  user: {
    id: number;
    first_name: string;
    last_name: string;
  };
  dismissal_reasons: DpeDismissalReasonType;
  is_dismissed: boolean;
  dismissed_by: {
    id: number;
    first_name: string;
    last_name: string;
  };
}

export interface ILogServerNoteItem {
  note: ILogServerNote;
}

export interface ILogNote {
  id: number;
  body: string;
  createdAt: IMoment;
  updatedAt: IMoment;
  isEdited: boolean;
  isVisibleToDriver: boolean;
  user: {
    id: number;
    name: string;
  };
  dismissalReasons?: DpeDismissalReasonType;
  isDismissed?: boolean;
  dismissedBy?: {
    id: number;
    name: string;
  };
}

export interface ILogCoachingData {
  coachingStatus: string;
  id: number;
  note?: ILogNote;
}

export interface ILogCoachingRequestData {
  coaching_status: string;
  coaching_dismissal_reasons?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class LogDetails {
  private isDriver: boolean;
  private isDispatcher: boolean;
  private observable: Observable<ILogDetailData>;
  private cachedLogsData: Map<number, ILog> = new Map();

  constructor(
    session: Session,
    private driversSearch: DriversSearch,
    private vehiclesSearch: VehiclesSearch,
    private assetsSearch: AssetsSearch,
    private api: ServerApi,
    private ffService: ComplianceFeatureFlagService,
  ) {
    this.isDriver = session.isDriver();
    this.isDispatcher = session.isDispatcher();
    this.cachedLogsData = new Map();
  }

  public get(id: number | string, params: object): Observable<ILogDetailData> {
    const mappedParams = objectToMap(params);
    mappedParams.set('include_sds_driving_period', true);

    let apiVer = this.api.API_VER_W3;

    if (this.ffService.isFedexVersionOfLogsEnabled()) {
      apiVer = this.api.API_VER_W4;
    }

    return this.api
      .get(`logs/${id}`, mappedParams, apiVer)
      .pipe(
        map(extractResponseBody),
        switchMap(this.mapData.bind(this)),
      )
    ;
  }

  public getOnce(id: number | string, params: object, forceRefresh: boolean = false): Observable<ILogDetailData> {
    if (!this.observable || forceRefresh) {
      this.observable = this.get(id, params)
        .pipe(
          publishReplay(1),
          refCount(),
          first(),
        )
      ;
    }
    return this.observable;
  }

  public updateCoachingStatus(id: number, data: ILogCoachingRequestData): Observable<ILogCoachingData> {
    return this.api.put(`logs/${id}`, data, null, this.api.API_VER_W3).pipe(
      map(extractResponseBody),
      map(this.mapCoachingData.bind(this)),
    );
  }

  public saveLogSuggestion(logSuggestion: LogSuggestion): Observable<{}> {
    const api = this.api;
    const data = logSuggestion.data;
    const id = data.id;
    let observable: Observable<{}>;
    if (id) {
      observable = api.put(`log_suggestions/${id}`, data);
    } else {
      observable = api.post('log_suggestions', data);
    }
    return observable;
  }

  public cancelLogSuggestion(id: number): Observable<{}> {
    return this.api.post(`log_suggestions/${id}/cancel`, null);
  }

  public ignoreDriveTime(id: number, data: IIgnoreDriveTimePayload[]): Observable<{}> {
    return this.api.put(`logs/${id}/events/ignore`, {
      events_attributes: data,
    });
  }

  public getLogNotes(id: number): Observable<ILogNote[]> {
    return this.api.get(`logs/${id}/notes`).pipe(
      map(extractResponseBody),
      map((data: { notes: ILogServerNoteItem[] }): ILogNote[] => {
        return (
          data.notes?.map((item: ILogServerNoteItem) => {
            const { note } = item;
            return this.mapNoteItem(note);
          }) || []
        );
      }),
    );
  }

  public getCachedLogsData(id: number, params: object): Observable<ILog> {
    if (this.cachedLogsData.has(id)) {
      return of(this.cachedLogsData.get(id));
    }

    return this.get(id, params).pipe(
      map((data: ILogDetailData): ILog => {
        this.cachedLogsData.set(id, data.log.viewModel);
        return data.log.viewModel;
      }),
    );
  }

  public clearCachedLogsData(): void {
    this.cachedLogsData = new Map();
  }

  public getLogsDetailNavigationInfo(logId: number, params: object): Observable<IServerNavigation> {
    const mappedParams = objectToMap(params);
    return this.api.get(`logs/${logId}/navigation_info`, mappedParams, this.api.API_VER_W3).pipe(
      map(extractResponseBody),
      map((data: {_navigation: IServerNavigation}) => data._navigation),
    );
  }

  private mapData(data: {log: IServerLogDetail}): Observable<ILogDetailData> {
    const logData = data.log;
    const log = new Log(logData);
    const navigationData: IServerNavigation = data.log._navigation;

    if (this.isDispatcher) {
      const logSuggestion = new LogSuggestion(logData.log_suggestion);
      const applyLogSuggestion$ = log.clone().applyLogSuggestion(
        logSuggestion,
        this.vehiclesSearch,
        this.driversSearch,
        this.assetsSearch,
      );
      return applyLogSuggestion$.pipe(map((logWithSuggestion: Log) => {
        return {
          log,
          logWithSuggestion,
          stagedLog: logWithSuggestion.clone(),
          logSuggestion,
          navigation: navigationData,
        };
      }));
    } else {
      const logSuggestion = new LogSuggestion(null);
      return of({
        log,
        logWithSuggestion: log.clone(),
        stagedLog: log.clone(),
        logSuggestion,
        navigation: navigationData,
      });
    }
  }

  private mapCoachingData(data: {log: ILogCoachingServerData}): ILogCoachingData {
    const coachingData = data.log;
    const note = coachingData?.note ? this.mapNoteItem(coachingData.note) : null;

    return {
      coachingStatus: coachingData.coaching_status,
      id: coachingData.id,
      note,
    };
  }

  private mapNoteItem(note: ILogServerNote): ILogNote {
    if (note.dismissal_reasons?.length) {
      note.user = {
        id: -1,
        first_name: 'Motive',
        last_name: '',
      };
    }
    return {
      id: note.id,
      body: note.body,
      createdAt: moment(note.created_at),
      updatedAt: moment(note.updated_at),
      isEdited: note.updated_at !== note.created_at,
      isVisibleToDriver: note.is_visible_to_driver,
      isDismissed: note.is_dismissed,
      user: {
        id: note.user.id,
        name: fullName(note.user),
      },
      dismissalReasons: note.dismissal_reasons?.length ? note.dismissal_reasons : null,
      dismissedBy: note.dismissed_by ?
        {
          id: note.dismissed_by.id,
          name: fullName(note.dismissed_by),
        } : null,
    };
  }

}
